import {
	useMutation,
	useQuery,
	useQueryClient,
	QueryObserverOptions,
} from '@tanstack/react-query'
import { useAuth } from '../auth/useAuth'
import {
	MfaService,
	MfaLoginRequest,
	MfaToggleRequest,
	MfaChallengeRequest,
	MfaVerifyRequest,
	MfaToggleResponse,
} from '../../lib/api/mfa.services'
import { handleApiError } from '../../lib/utils/handleApiError'
import toastCaller from '../../lib/utils/toastCaller'

/**
 * Hook to get MFA status for current user
 */
export const useMfaStatus = (
	options?: Partial<
		QueryObserverOptions<{ success: boolean; data: { value: boolean } }>
	>
) => {
	const { isAuthenticated } = useAuth()
	return useQuery({
		queryKey: ['mfa-status'],
		queryFn: () => MfaService.getStatus(),
		enabled: isAuthenticated,
		retry: false,
		...options,
	})
}

/**
 * Hook to login with username/password (first step of MFA flow)
 */
export const useMfaLogin = () => {
	const mutation = useMutation({
		mutationKey: ['mfa-login'],
		mutationFn: (data: MfaLoginRequest) => MfaService.login(data),
		onError: (error) => {
			// Don't show error toast for mfa_required - it's expected
			if (
				error &&
				typeof error === 'object' &&
				'error' in error &&
				error.error === 'mfa_required'
			) {
				return
			}
			handleApiError(error)
		},
	})

	return mutation
}

/**
 * Hook to trigger MFA challenge (push/sms/email)
 */
export const useMfaChallenge = () => {
	const mutation = useMutation({
		mutationKey: ['mfa-challenge'],
		mutationFn: (data: MfaChallengeRequest) => MfaService.challenge(data),
		onError: (error) => handleApiError(error),
	})

	return mutation
}

/**
 * Hook to verify OTP code
 */
export const useMfaVerify = () => {
	const mutation = useMutation({
		mutationKey: ['mfa-verify'],
		mutationFn: (data: MfaVerifyRequest) => MfaService.verifyOtp(data),
		onError: (error) => handleApiError(error),
	})

	return mutation
}

/**
 * Hook to toggle MFA on/off
 * Supports both password-based and OTP-based flows
 */
export const useMfaToggle = () => {
	const mutation = useMutation<MfaToggleResponse, unknown, MfaToggleRequest>({
		mutationKey: ['mfa-toggle'],
		mutationFn: (data: MfaToggleRequest) => MfaService.toggle(data),
	})

	const mutateAsync = async (
		variables: MfaToggleRequest
	): Promise<MfaToggleResponse | undefined> => {
		try {
			const result = await mutation.mutateAsync(variables)
			return result
		} catch (error) {
			handleApiError(error)
			return undefined
		}
	}

	return { ...mutation, mutateAsync }
}

/**
 * Hook to skip MFA setup (post-registration)
 */
export const useMfaSkip = () => {
	const queryClient = useQueryClient()

	const mutation = useMutation({
		mutationKey: ['mfa-skip'],
		mutationFn: () => MfaService.skip(),
		onError: (error) => handleApiError(error),
		onSuccess: () => {
			toastCaller({
				type: 'info',
				message: 'MFA setup skipped.',
				autoClose: 2000,
			})
			queryClient.invalidateQueries({ queryKey: ['mfa-status'] })
			queryClient.invalidateQueries({ queryKey: ['user'] })
		},
	})

	return mutation
}
